import { del, get, patch, post, put } from './client';
import type {
  BackupOverview,
  AccountAchievements,
  AchievementStatistics,
  AdminAccount,
  AdminChild,
  AuditEntry,
  AuthenticationResult,
  ChallengeDefinition,
  ChallengeDashboard,
  ChallengeHealth,
  ChildAchievements,
  ChildDashboard,
  Paginated,
  PlatformActivity,
  PlatformStatistics,
  ReadingCorpusStatistics,
  ReadingStoryDetail,
  ReadingStorySummary,
  SubscriptionPlanView,
} from './types';

export const login = (email: string, password: string): Promise<AuthenticationResult> =>
  post<AuthenticationResult>('/auth/login', { email, password });

export const readMe = (): Promise<AuthenticationResult['account']> =>
  get<AuthenticationResult['account']>('/auth/me');

export const logout = (): Promise<{ revoked: number }> => post<{ revoked: number }>('/auth/logout', {});

export const readStatistics = (days: number): Promise<PlatformStatistics> =>
  get<PlatformStatistics>(`/dashboard?days=${days}`);

export const readActivity = (days: number): Promise<PlatformActivity> =>
  get<PlatformActivity>(`/dashboard/activity?days=${days}`);

export const readContentHealth = (days: number): Promise<ChallengeHealth> =>
  get<ChallengeHealth>(`/dashboard/content-health?days=${days}`);

export const suspendAccount = (accountId: string, reason: string): Promise<AdminAccount> =>
  post<AdminAccount>(`/accounts/${accountId}/suspend`, { reason });

export const reactivateAccount = (accountId: string): Promise<AdminAccount> =>
  post<AdminAccount>(`/accounts/${accountId}/reactivate`, {});

export const createAccount = (
  email: string,
  role: string,
  password: string,
  displayName?: string,
): Promise<AdminAccount> => post<AdminAccount>('/accounts', { email, role, password, displayName });

/**
 * Replace an account's password. The old one stops working immediately.
 *
 * The password is generated in the browser and sent, rather than generated by the server and returned: a
 * secret the server invents has already been through a response body, a log and a proxy before anybody
 * decides whether it was needed (R0142).
 */
export const resetAccountPassword = (accountId: string, password: string): Promise<AdminAccount> =>
  post<AdminAccount>(`/accounts/${accountId}/password`, { password });

export const changeAccountRole = (accountId: string, role: string): Promise<AdminAccount> =>
  patch<AdminAccount>(`/accounts/${accountId}/role`, { role });

export const searchAccounts = (
  term: string,
  page: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  role?: string,
  status?: string,
  locale?: string,
): Promise<Paginated<AdminAccount>> => {
  const query = new URLSearchParams({ term, page: String(page) });

  if (sort) query.set('sort', sort);
  if (direction) query.set('direction', direction);
  if (role) query.set('role', role);
  if (status) query.set('status', status);
  if (locale) query.set('locale', locale);

  return get<Paginated<AdminAccount>>(`/accounts?${query.toString()}`);
};

export const readAccountChildren = (accountId: string): Promise<AdminChild[]> =>
  get<AdminChild[]>(`/accounts/${accountId}/children`);

export const searchChildren = (
  term: string,
  page: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  grade?: number,
  status?: string,
): Promise<Paginated<AdminChild>> => {
  const query = new URLSearchParams({ term, page: String(page) });

  if (sort) query.set('sort', sort);
  if (direction) query.set('direction', direction);
  if (grade !== undefined) query.set('grade', String(grade));
  if (status) query.set('status', status);

  return get<Paginated<AdminChild>>(`/children?${query.toString()}`);
};

export const readChildDashboard = (childId: string): Promise<ChildDashboard> =>
  get<ChildDashboard>(`/children/${childId}/dashboard`);

export const readChildAchievements = (childId: string): Promise<ChildAchievements> =>
  get<ChildAchievements>(`/children/${childId}/achievements`);

export const readAccountAchievements = (accountId: string): Promise<AccountAchievements[]> =>
  get<AccountAchievements[]>(`/accounts/${accountId}/achievements`);

export const readAchievementStatistics = (days: number): Promise<AchievementStatistics> =>
  get<AchievementStatistics>(`/achievements/statistics?days=${days}`);

export const readChallenges = (): Promise<ChallengeDefinition[]> => get<ChallengeDefinition[]>('/challenges');

export const toggleChallenge = (
  code: string,
  isEnabled: boolean,
  notes: string,
): Promise<ChallengeDefinition> =>
  put<ChallengeDefinition>(`/challenges/${encodeURIComponent(code)}`, {
    isEnabled,
    notes: notes || undefined,
  });

/**
 * Sorting and filtering are **server-side parameters**, not something applied to the rows that came back.
 *
 * The table is paginated, so ordering the twenty-five rows on screen would order the page and call it
 * sorted — which looks like it worked and is not (R0109).
 */
export const searchStories = (
  term: string,
  page: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  level?: number,
  status?: string,
): Promise<Paginated<ReadingStorySummary>> => {
  const query = new URLSearchParams({ term, page: String(page) });

  if (sort) query.set('sort', sort);
  if (direction) query.set('direction', direction);
  if (level !== undefined) query.set('level', String(level));
  if (status) query.set('status', status);

  return get<Paginated<ReadingStorySummary>>(`/reading-stories?${query.toString()}`);
};

export const readChallengeDashboard = (code: string, days: number): Promise<ChallengeDashboard> =>
  get<ChallengeDashboard>(`/challenges/${encodeURIComponent(code)}/dashboard?days=${days}`);

export const readStory = (storyId: string): Promise<ReadingStoryDetail> =>
  get<ReadingStoryDetail>(`/reading-stories/${encodeURIComponent(storyId)}`);

export const readCorpusStatistics = (): Promise<ReadingCorpusStatistics> =>
  get<ReadingCorpusStatistics>('/reading-stories/statistics');

export const readPlans = (): Promise<SubscriptionPlanView[]> =>
  get<SubscriptionPlanView[]>('/subscription-plans');

export const savePlan = (plan: unknown): Promise<SubscriptionPlanView> =>
  post<SubscriptionPlanView>('/subscription-plans', plan);

/** Which sign-in providers the back office offers. The buttons are rendered from this, never guessed. */
export const readAuthProviders = (): Promise<string[]> => get<string[]>('/auth/providers');

export const readBackups = (): Promise<BackupOverview> => get<BackupOverview>('/backups');

export const readAudit = (
  page: number,
  sort?: string,
  direction?: 'asc' | 'desc',
  action?: string,
  targetType?: string,
): Promise<Paginated<AuditEntry>> => {
  const query = new URLSearchParams({ page: String(page) });

  if (sort) query.set('sort', sort);
  if (direction) query.set('direction', direction);
  if (action) query.set('action', action);
  if (targetType) query.set('targetType', targetType);

  return get<Paginated<AuditEntry>>(`/audit?${query.toString()}`);
};

/**
 * Discard a child's session for today, so the next one they start is freshly composed.
 *
 * A staff tool for testing challenges more than once a day: a child gets one session daily, which is the
 * product working correctly and means every change to a challenge otherwise takes a day to see (R0129).
 */
export const resetChildTrainingToday = (childId: string): Promise<{ discarded: boolean }> =>
  del<{ discarded: boolean }>(`/children/${childId}/training/today`);
